import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import DataTable from '../../../components/ui/DataTable';
import { usePeriodExtensions } from '../config/participants';

const Grid = styled.dl`
  display: grid;
  grid-template-columns: minmax(160px, 220px) 1fr;
  gap: 10px 16px;
  margin: 16px 0;
  padding: 20px;
  background: ${({ theme }) => theme.colors.surface.card};
  border: 1px solid ${({ theme }) => theme.colors.surface.border};
  border-radius: 12px;

  dt {
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: 13px;
  }

  dd {
    margin: 0;
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: 14px;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('tr-TR') : '—');

const ExtendedTag = styled.span`
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  background: ${({ theme }) => theme.colors.surface.border};
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Spec 2026-10-05 §15/4 — dönem detayının Genel sekmesi (salt okunur özet).
// Spec 2026-10-09 §9 — pencere tarihleri + uzatılmışsa işaret ve Süre Uzatma Geçmişi.
export default function PeriodOverviewTab({ period }) {
  const { t } = useTranslation();
  const { extensions, fetching } = usePeriodExtensions(period?.id);

  const columns = useMemo(() => [
    { name: 'createDate', label: t('performance.period.detail.ext.date'), options: { filter: false, sort: false, customBodyRender: formatDate } },
    { name: 'extendedByName', label: t('performance.period.detail.ext.by'), options: { filter: false, sort: false, customBodyRender: (v) => v || '—' } },
    {
      name: 'id', label: t('performance.period.detail.ext.change'),
      options: {
        filter: false, sort: false,
        customBodyRender: (_, meta) => {
          const r = extensions[meta.rowIndex];
          if (!r) return '—';
          const parts = [];
          if (r.newEvalWindowEnd) parts.push(`${t('performance.period.detail.ext.evalEnd')}: ${formatDate(r.oldEvalWindowEnd)} → ${formatDate(r.newEvalWindowEnd)}`);
          if (r.newCalibWindowStart) parts.push(`${t('performance.period.detail.ext.calibStart')}: ${formatDate(r.oldCalibWindowStart)} → ${formatDate(r.newCalibWindowStart)}`);
          if (r.newCalibWindowEnd) parts.push(`${t('performance.period.detail.ext.calibEnd')}: ${formatDate(r.oldCalibWindowEnd)} → ${formatDate(r.newCalibWindowEnd)}`);
          return parts.join(' · ');
        },
      },
    },
    { name: 'reasonCode', label: t('performance.period.detail.ext.reason'), options: { filter: false, sort: false, customBodyRender: (v) => t(`performance.period.extend.reason.${v}`, { defaultValue: v }) } },
    { name: 'note', label: t('performance.period.detail.ext.note'), options: { filter: false, sort: false, customBodyRender: (v) => v || '—' } },
  ], [t, extensions]);

  if (!period) return null;

  const systems = [
    period.systemAEnabled && t('performance.period.detail.systemA'),
    period.systemBEnabled && t('performance.period.detail.systemB'),
  ].filter(Boolean).join(', ');

  return (
    <>
      <Grid>
        <dt>{t('performance.period.name')}</dt>
        <dd>{period.name}</dd>
        <dt>{t('performance.period.detail.statusLabel')}</dt>
        <dd>
          {t(`performance.period.status.${period.status}`, { defaultValue: period.status })}
          {extensions.length > 0 && <ExtendedTag>{t('performance.period.detail.extended')}</ExtendedTag>}
        </dd>
        <dt>{t('performance.period.detail.dates')}</dt>
        <dd>{formatDate(period.startDate)} – {formatDate(period.endDate)}</dd>
        <dt>{t('performance.period.detail.evalWindow')}</dt>
        <dd>{formatDate(period.evalWindowStart)} – {formatDate(period.evalWindowEnd)}</dd>
        <dt>{t('performance.period.detail.calibWindow')}</dt>
        <dd>{formatDate(period.calibWindowStart)} – {formatDate(period.calibWindowEnd)}</dd>
        <dt>{t('performance.period.detail.systems')}</dt>
        <dd>{systems || '—'}</dd>
        <dt>{t('performance.period.detail.scope')}</dt>
        <dd>{t(`performance.period.detail.scopeType.${period.scopeType}`, { defaultValue: period.scopeType })}</dd>
      </Grid>
      {extensions.length > 0 && (
        <DataTable
          title={t('performance.period.detail.extensionsTitle')}
          fetching={fetching}
          columns={columns}
          data={extensions}
          totalRowCount={extensions.length}
          pageIndex={0}
          pageSize={Math.max(extensions.length, 10)}
        />
      )}
    </>
  );
}
